import { useState } from 'react';

import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PropTypes from 'prop-types';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import Button from '@zenbusiness/zui/Button';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import { THREAD_MODES } from '#client/components/utils/constants';

import * as Styled from './ProfileMenu.style';
/**
 * @typedef {object} ProfileUser
 * @property {string} [firstName] - User's first name
 * @property {string} [lastName] - User's last name
 * @property {string} [email] - User's email address
 */

/**
 * User Profile Menu Component
 * Shows Login button for anonymous users, and profile menu with logout for authenticated users
 *
 * @param {object} props - Component props
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {ProfileUser} [props.user] - User information (required when authenticated)
 * @param {Function} [props.onLogin] - Callback when user clicks login (opens login modal)
 * @param {string} [props.threadMode] - The thread mode (anonymous/starter/business/standalone)
 * @returns {import('react').ReactElement} The ProfileMenu component
 */
function ProfileMenu({ isAuthenticated, user, onLogin, threadMode }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const { logout } = useAuthentication();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const enableVeloTestMode = useExperimentFlag('enable-velo-test-mode', true);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogin = () => {
    handleClose();
    if (onLogin) {
      onLogin();
    }
  };

  const handleLogout = () => {
    handleClose();
    logout('/');
  };

  // Full name display
  const fullName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.email || 'User';

  return (
    <>
      <Styled.ProfileButton
        aria-controls={open ? 'profile-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        aria-label="User profile menu"
        onClick={handleOpen}
      >
        <PersonOutlineIcon fontSize="small" />
      </Styled.ProfileButton>

      <Styled.ProfileMenuDropdown
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isMobile ? 'left' : 'right'
        }}
        id="profile-menu"
        open={open}
        slotProps={{
          paper: {
            sx: isMobile
              ? {
                  width: 'min(95vw, calc(100vw - 2rem))',
                  maxWidth: 'min(95vw, calc(100vw - 2rem))',
                  left: 0,
                  right: 0,
                  borderRadius: 1,
                  mt: 1
                }
              : {
                  minWidth: '17.5rem',
                  borderRadius: 1,
                  mt: 1
                }
          }
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isMobile ? 'left' : 'right'
        }}
        onClose={handleClose}
      >
        {/* User Info Section */}
        <Styled.ProfileContainer>
          {isAuthenticated && (
            <Styled.UserInfoContainer>
              <Text fontWeight="bold" size="lg">
                {fullName}
              </Text>
              {user?.email && <Text size="sm">{user.email}</Text>}
            </Styled.UserInfoContainer>
          )}
          {/* Logout Button */}
          {isAuthenticated ? (
            <Styled.ButtonContainer>
              {(enableVeloTestMode || threadMode !== THREAD_MODES.BUSINESS) && (
                <Button fullWidth onClick={handleLogout}>
                  Log out
                </Button>
              )}
            </Styled.ButtonContainer>
          ) : (
            <Styled.ButtonContainer>
              <Button fullWidth onClick={handleLogin}>
                Log in
              </Button>
            </Styled.ButtonContainer>
          )}
        </Styled.ProfileContainer>
      </Styled.ProfileMenuDropdown>
    </>
  );
}

ProfileMenu.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string
  }),
  onLogin: PropTypes.func,
  threadMode: PropTypes.oneOf(Object.values(THREAD_MODES))
};

export default ProfileMenu;
